/**
 * Easy Print Wizard (iteration-2 scaling, round-6 scope semantics). Offered
 * for large batches (≥40 ready photos while any photo still has no prints):
 * pick one size → paper/finish → qty per photo → border → summary, then apply
 * that single config and route to the cart.
 *
 * SCOPE (round 6, see lib/sizePaper.wizardScopeFor):
 *  - fresh order (no prints yet): applies to every ready photo;
 *  - SOME photos already configured: applies ONLY to the printless photos and
 *    the summary says existing prints are not changed;
 *  - EVERY photo configured (explicit chip re-run): the summary asks
 *    "Replace everything with this new setup?" and apply is REPLACE-ALL.
 *
 * The summary also flags aspect mismatches BEFORE apply ("12 of 59 photos
 * don't match 4×6 and will be auto-cropped") — no silent auto-crop. The
 * preview pages through the scoped photos with ‹ › arrows (buttons +
 * keyboard). "Edit manually" dismisses to the normal Step 2.
 *
 * ROUND 7 — wizard → review handoff: when the apply creates prints that need
 * review (auto-crops / low-res), the wizard lands on a COMPLETION state and
 * AUTO-OPENS the same Review-crops dialog used by the Continue-! flow, scoped
 * to the newly flagged prints (lib/sizePaper.reviewScopeFor). The completion
 * state keeps a "Review crops now" button (re-open) and "Go to cart →".
 * A clean apply (no flags) routes straight to the cart as before — UNLESS
 * the enlargement finale below has an offer to make.
 *
 * UPSELL #9 (owner 2026-08-15) — enlargement finale: after the apply, the
 * done screen offers ONE 8×12 of the batch's sharpest shot (wizardEnlargement
 * in lib/upsell — goodDpi-gated, skipped when the wizard size is already
 * 8×12+). One card, one button, absolute "+$X" price; accepting adds a single
 * extra print via ensurePrint. Wizard applies also mark frameNudgeSeen so the
 * post-Add frame sheet (#1) never stacks on top of this.
 */

import {
  borderModesFor,
  parseSizeId,
  type BorderMode,
  type PaperId,
  type PricingData,
  type ResolvedSize,
} from '@rpl/pricing';
import { useEffect, useMemo, useState } from 'react';
import { reportUpsell } from '../../lib/upsellReport';
import { formatMoney } from '../../lib/format';
import { wizardEnlargement, type WizardEnlargement } from '../../lib/upsell';
import {
  borderOptionLabel,
  countRatioMismatch,
  defaultCtPaper,
  isCtPaper,
  isPaperValidForSize,
  paperForSize,
  paperPickerGroups,
  paperRestrictionNote,
  priceForSafe,
  reviewScopeFor,
  shortPaperLabel,
  sizePickerGroups,
  sizeShort,
  wizardScopeFor,
} from '../../lib/sizePaper';
import type { LocalPhoto } from '../../lib/types';
import { useStore } from '../../store/store';
import { Dropdown } from './Dropdown';
import { ModalShell } from './ModalShell';
import { PaperInfoDialog } from './PaperInfoDialog';
import { PrintPreview } from './PrintPreview';
import { QtyStepper } from './QtyStepper';
import { Segmented } from './Segmented';
import type { OrderActions } from './useOrderActions';

export interface EasyWizardModalProps {
  pricing: PricingData;
  catalog: Map<string, ResolvedSize>;
  actions: OrderActions;
  photoCount: number;
  /** Finish: close the wizard, toast, route to the cart. */
  onApply: (touchedIds: string[]) => void;
  /** Open the Review-crops dialog SCOPED to these freshly-flagged print ids. */
  onReview: (flaggedIds: string[]) => void;
  onDismiss: () => void;
}

type Stage = 'intro' | 'size' | 'paper' | 'qty' | 'border' | 'summary' | 'done';
const FLOW: Stage[] = ['intro', 'size', 'paper', 'qty', 'border', 'summary'];

export function EasyWizardModal({
  pricing,
  catalog,
  actions,
  photoCount,
  onApply,
  onReview,
  onDismiss,
}: EasyWizardModalProps) {
  const photos = useStore((s) => s.photos);
  const prints = useStore((s) => s.prints);
  const readyPhotos = useMemo(() => photos.filter((p) => p.status === 'ready'), [photos]);

  /* ---------- scope: printless photos only, or explicit replace-all ---------- */
  const plan = useMemo(
    () => wizardScopeFor(readyPhotos.map((p) => p.id), prints),
    [readyPhotos, prints],
  );
  const scopePhotos = useMemo(() => {
    const ids = new Set(plan.targetIds);
    return readyPhotos.filter((p) => ids.has(p.id));
  }, [readyPhotos, plan]);
  const scopeCount = scopePhotos.length;

  const [stage, setStage] = useState<Stage>('intro');
  const [sizeId, setSizeId] = useState<string>(catalog.has('ct-4x6') ? 'ct-4x6' : [...catalog.keys()][0] ?? 'ct-4x6');
  const [paper, setPaper] = useState<PaperId>('gloss');
  const [qty, setQty] = useState(1);
  const [border, setBorder] = useState<BorderMode>('none');
  const [paperInfoOpen, setPaperInfoOpen] = useState(false);

  /* ---------- summary preview pager (‹ › through the SCOPED photos) ---------- */
  const [previewIdx, setPreviewIdx] = useState(0);
  const count = scopeCount;
  const idx = count > 0 ? Math.min(previewIdx, count - 1) : 0;
  const previewPhoto = count > 0 ? scopePhotos[idx] : null;
  const stepPreview = (delta: -1 | 1) => {
    if (count < 2) return;
    setPreviewIdx((idx + delta + count) % count);
  };

  // Keyboard ‹ › on the summary step (no other arrow-consuming control there).
  useEffect(() => {
    if (stage !== 'summary' || count < 2) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setPreviewIdx((i) => (Math.min(i, count - 1) - 1 + count) % count);
      else if (e.key === 'ArrowRight') setPreviewIdx((i) => (Math.min(i, count - 1) + 1) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage, count]);

  const entry = catalog.get(sizeId);
  const isFineArt = parseSizeId(sizeId).family === 'fine_art';
  const price = priceForSafe(pricing, sizeId, paper);
  const sizeGroups = useMemo(
    () => sizePickerGroups(pricing, catalog, paper, { currentSizeId: sizeId }),
    [pricing, catalog, paper, sizeId],
  );
  const paperGroups = useMemo(
    () => (isFineArt ? paperPickerGroups(pricing, sizeId, paper) : []),
    [pricing, isFineArt, sizeId, paper],
  );
  const ctPapers = pricing.productLines.ctype_standard.papers;
  const borderModes = borderModesFor(sizeId, false);
  /** "8×12 prints on Lustre only" — shown when the size restricts its finishes. */
  const restrictionNote = paperRestrictionNote(pricing, sizeId);

  const idxStage = FLOW.indexOf(stage);
  const lineTotal = (price ?? 0) * qty * scopeCount;

  /* ---------- pre-apply ratio warning (no silent auto-crop) ---------- */
  const mismatchCount = useMemo(
    () => (entry ? countRatioMismatch(scopePhotos, entry.inches, actions.thresholds.ratioTolerance) : 0),
    [scopePhotos, entry, actions.thresholds],
  );

  const pickSize = (key: string) => {
    setSizeId(key);
    setPaper(paperForSize(pricing, key, paper));
    setBorder('none');
  };

  const back = () => setStage(FLOW[Math.max(0, idxStage - 1)]);
  const next = () => setStage(FLOW[Math.min(FLOW.length - 1, idxStage + 1)]);

  /* ---------- apply + review handoff (round 7) + enlargement finale (#9) ---------- */
  const [applied, setApplied] = useState<{ ids: string[]; flaggedIds: string[] } | null>(null);
  const [offer, setOffer] = useState<WizardEnlargement<LocalPhoto> | null>(null);
  const [offerTaken, setOfferTaken] = useState(false);

  const apply = () => {
    const ids = actions.bulkApplyAll(sizeId, paper, qty, border, plan.scope);
    // A wizard order has answered the "one big one?" question here — the
    // post-Add frame sheet (#1) must not ask again on the next manual add.
    // wizardApplied also blocks the cart small-prints sheet and locks the
    // anchor default out (bulk orders keep their own economics).
    useStore.getState().setFrameNudgeSeen(true);
    useStore.getState().setWizardApplied();
    // The prints the apply just created that need review (auto-crop / low-res).
    const flaggedIds = reviewScopeFor(ids, useStore.getState().prints);
    const enlarge = wizardEnlargement(catalog, scopePhotos, actions.thresholds, sizeId);
    if (flaggedIds.length === 0 && !enlarge) {
      onApply(ids); // clean apply, nothing to offer — straight to the cart
      return;
    }
    if (enlarge) reportUpsell('shown', 'wizard_enlarge', enlarge.entry.sizeId);
    setOffer(enlarge);
    setApplied({ ids, flaggedIds });
    setStage('done');
    if (flaggedIds.length > 0) onReview(flaggedIds); // auto-open the scoped Review dialog
  };

  const offerPrice = offer ? priceForSafe(pricing, offer.entry.sizeId, paperForSize(pricing, offer.entry.sizeId, paper)) : null;
  const takeOffer = () => {
    if (!offer || offerTaken) return;
    const pid = actions.ensurePrint(offer.photo.id, offer.entry.sizeId, paper, 'none');
    if (!pid) return;
    setOfferTaken(true);
    reportUpsell('accepted', 'wizard_enlarge', offer.entry.sizeId);
  };

  const applyLabel = plan.replacesAll
    ? `Replace all ${scopeCount} photos →`
    : `Apply to ${scopeCount} photo${scopeCount === 1 ? '' : 's'} →`;

  return (
    <ModalShell onClose={onDismiss} width="min(720px, 92vw)" label="Easy Print Wizard" className="wiz">
      <div className="wiz__head">
        <div>
          <div className="wiz__title">Easy Print Wizard</div>
          <div className="wiz__sub">
            {stage === 'intro'
              ? `That's a big batch (${photoCount} photos).`
              : stage === 'done'
                ? 'All set'
                : `Step ${idxStage} of ${FLOW.length - 1} · applies to ${scopeCount} photo${scopeCount === 1 ? '' : 's'}`}
          </div>
        </div>
        <button type="button" className="wiz__close" aria-label="Close" onClick={onDismiss}>
          ✕
        </button>
      </div>

      <div className="wiz__body">
        {stage === 'intro' && (
          <div className="wiz__intro">
            <p>
              Want to set a size, paper and quantity <strong>once</strong> and apply it to every photo? You
              can still tweak single prints afterwards.
            </p>
            {plan.preservesExisting && (
              <p className="wiz__scope-hint">
                {scopeCount} of your {readyPhotos.length} photos have no prints yet. This will set up just
                those. Your existing prints are not changed.
              </p>
            )}
          </div>
        )}

        {stage === 'size' && (
          <div className="wiz__field">
            <div className="eyebrow">Size</div>
            <Dropdown
              variant="light"
              ariaLabel="Print size"
              groups={sizeGroups}
              value={sizeId}
              buttonLabel={entry ? `${entry.label}${price != null ? ` · ${formatMoney(price)}` : ''}` : 'Choose a size'}
              onChange={pickSize}
            />
          </div>
        )}

        {stage === 'paper' && (
          <div className="wiz__field">
            <div className="cfg__label-row">
              <div className="eyebrow">{isFineArt ? 'Paper' : 'Finish'}</div>
              <button type="button" className="paper-chip" onClick={() => setPaperInfoOpen(true)}>
                ⓘ Paper Details
              </button>
            </div>
            {isFineArt ? (
              <Dropdown
                variant="light"
                ariaLabel="Fine art paper"
                groups={paperGroups}
                value={paper}
                buttonLabel={shortPaperLabel(pricing, paper)}
                onChange={(key) => setPaper(key as PaperId)}
              />
            ) : (
              <Segmented
                variant="light"
                className="wiz__finish"
                ariaLabel="Finish"
                segments={ctPapers.map((p) => ({
                  key: p,
                  label: pricing.papers[p]?.label ?? p,
                  // Per-size restriction: 8×12 offers Lustre only, so Gloss greys out.
                  disabled: !isPaperValidForSize(pricing, sizeId, p),
                }))}
                value={isCtPaper(pricing, paper) ? paper : defaultCtPaper(pricing)}
                onChange={(key) => setPaper(key as PaperId)}
              />
            )}
            {restrictionNote && <span className="paper-note">{restrictionNote}</span>}
          </div>
        )}

        {stage === 'qty' && (
          <div className="wiz__field wiz__field--row">
            <div className="eyebrow">Quantity per photo</div>
            <QtyStepper variant="light" ariaLabel="Quantity per photo" value={qty} onChange={setQty} />
          </div>
        )}

        {stage === 'border' && (
          <div className="wiz__field">
            <div className="eyebrow">Border</div>
            <Segmented
              variant="light"
              className="wiz__border"
              ariaLabel="Border style"
              segments={borderModes.map((m) => ({ key: m, label: borderOptionLabel(pricing, sizeId, m) }))}
              value={border}
              onChange={(key) => setBorder(key as BorderMode)}
            />
          </div>
        )}

        {stage === 'summary' && (
          <div className="wiz__summary">
            {previewPhoto && entry && (
              <>
                <div className="wiz__preview-stage">
                  <PrintPreview
                    photo={previewPhoto}
                    sizeId={sizeId}
                    inches={entry.inches}
                    fitMode="crop"
                    cropRect={null}
                    borderMode={border}
                    pricing={pricing}
                    className="wiz__preview"
                  />
                  {count > 1 && (
                    <>
                      <button
                        type="button"
                        className="cfg__preview-nav cfg__preview-nav--prev"
                        aria-label="Previous photo"
                        onClick={() => stepPreview(-1)}
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        className="cfg__preview-nav cfg__preview-nav--next"
                        aria-label="Next photo"
                        onClick={() => stepPreview(1)}
                      >
                        ›
                      </button>
                    </>
                  )}
                </div>
                <span className="wiz__preview-cap">
                  {count > 1 ? `${idx + 1} of ${count} · ${previewPhoto.filename}` : previewPhoto.filename}
                </span>
              </>
            )}
            <ul className="wiz__recap">
              <li>
                <span>Size</span>
                <strong>{entry ? entry.label : sizeId}</strong>
              </li>
              <li>
                <span>{isFineArt ? 'Paper' : 'Finish'}</span>
                <strong>{shortPaperLabel(pricing, paper)}</strong>
              </li>
              <li>
                <span>Quantity</span>
                <strong>
                  {qty} × {scopeCount} photos = {qty * scopeCount} prints
                </strong>
              </li>
              <li>
                <span>Border</span>
                <strong>{borderOptionLabel(pricing, sizeId, border)}</strong>
              </li>
              <li className="wiz__recap-total">
                <span>Estimated</span>
                <strong>{formatMoney(lineTotal)}</strong>
              </li>
            </ul>
            {mismatchCount > 0 && entry && (
              <p className="wiz__ratio-note" role="note">
                {mismatchCount} of {scopeCount} photos don&rsquo;t match {sizeShort(entry.size)} and will be
                auto-cropped. You can adjust crops after.
              </p>
            )}
            {plan.preservesExisting && (
              <p className="wiz__scope-note" role="note">
                This bulk action only applies to your newly added photos. Your existing prints are not
                changed.
              </p>
            )}
            {plan.replacesAll && (
              <p className="wiz__replace-note" role="note">
                Replace everything with this new setup? This replaces any prints you&rsquo;ve already set up
                on these photos.
              </p>
            )}
          </div>
        )}

        {stage === 'done' && applied && (
          <div className="wiz__done">
            <span className="wiz__done-tick" aria-hidden>
              ✓
            </span>
            <p className="wiz__done-title">
              Set up {applied.ids.length} photo{applied.ids.length === 1 ? '' : 's'}.
            </p>
            {applied.flaggedIds.length > 0 && (
              <p className="wiz__ratio-note" role="note">
                {applied.flaggedIds.length} print{applied.flaggedIds.length === 1 ? '' : 's'} need
                {applied.flaggedIds.length === 1 ? 's' : ''} a quick look — we&rsquo;ve auto-cropped photos
                that don&rsquo;t match {entry ? sizeShort(entry.size) : 'the size'}. Confirm each crop or
                switch to Fit in the review screen.
              </p>
            )}
            {offer && offerPrice != null && (
              <div className="wiz__offer">
                {offer.photo.previewUrl && (
                  <img className="wiz__offer-thumb" src={offer.photo.previewUrl} alt="" />
                )}
                <div className="wiz__offer-txt">
                  <div className="wiz__offer-title">One to frame?</div>
                  <p className="wiz__offer-detail">
                    Your sharpest shot would make a beautiful {sizeShort(offer.entry.size)} enlargement.
                  </p>
                </div>
                <button
                  type="button"
                  className={offerTaken ? 'btn btn--secondary' : 'btn btn--primary'}
                  disabled={offerTaken}
                  onClick={takeOffer}
                >
                  {offerTaken ? 'Added ✓' : `Add one · +${formatMoney(offerPrice)}`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="wiz__foot">
        {stage === 'done' ? (
          <>
            <span />
            <div className="wiz__nav">
              {applied && applied.flaggedIds.length > 0 && (
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => onReview(applied.flaggedIds)}
                >
                  Review crops now
                </button>
              )}
              <button type="button" className="btn btn--gold" onClick={() => applied && onApply(applied.ids)}>
                Go to cart →
              </button>
            </div>
          </>
        ) : (
          <>
            <button type="button" className="wiz__manual" onClick={onDismiss}>
              Edit manually
            </button>
            <div className="wiz__nav">
              {idxStage > 0 && (
                <button type="button" className="btn btn--secondary" onClick={back}>
                  ← Back
                </button>
              )}
              {stage === 'summary' ? (
                <button type="button" className="btn btn--gold" onClick={apply}>
                  {applyLabel}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={next}
                  disabled={stage === 'size' && !entry}
                >
                  {stage === 'intro' ? 'Get started →' : 'Next →'}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {paperInfoOpen && (
        <PaperInfoDialog
          pricing={pricing}
          onClose={() => setPaperInfoOpen(false)}
          select={{ sizeId, paper, onPick: (p) => setPaper(paperForSize(pricing, sizeId, p)) }}
        />
      )}
    </ModalShell>
  );
}
